import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import Services from '../components/Services';

const quickLinks = [
  {
    title: 'About',
    copy: 'See how the studio thinks about land, materials, and long-term value.',
    to: '/about',
  },
  {
    title: 'Projects',
    copy: 'Browse the current portfolio and the kind of addresses we are shaping now.',
    to: '/projects',
  },
  {
    title: 'Contact',
    copy: 'Start a private conversation about a site, a purchase, or a future build.',
    to: '/contact',
  },
];

const approachPoints = [
  {
    title: 'Acquisition Discipline',
    copy: 'Site selection built on circulation, adjacency, and resale resilience instead of vanity frontage.',
  },
  {
    title: 'Design Governance',
    copy: 'Every elevation and material spec is checked against long-term maintenance and liveability.',
  },
  {
    title: 'Delivery Stewardship',
    copy: 'Transparent schedules, controlled vendor sets, and a single point of accountability.',
  },
];

const marketStats = [
  { label: 'Active site reviews', value: '12' },
  { label: 'Premium corridors tracked', value: '9' },
  { label: 'Average decision window', value: '21 days' },
];

const testimonials = [
  {
    quote:
      'Their oversight kept the design language consistent from brochure to handover. The final walk-through felt identical to the vision deck.',
    name: 'R. Kulkarni',
    role: 'Private Residence Client',
  },
  {
    quote:
      'We appreciated the discipline on specifications and vendor control. It turned a complex commercial build into a predictable delivery.',
    name: 'N. Prasad',
    role: 'Commercial Anchor Partner',
  },
  {
    quote:
      'The team understood how privacy, circulation, and light should feel rather than just how it should look.',
    name: 'M. Fernandes',
    role: 'Independent Architect',
  },
];

const pressHighlights = [
  { title: 'Private Land Acquisition Playbooks', source: 'Studio Briefing' },
  { title: 'How to Build a Calm Entry Sequence', source: 'Design Review' },
  { title: 'Measured Luxury: Avoiding Overstatement', source: 'Builder Insight' },
];

const awards = [
  { title: 'Design Stewardship Award', year: '2024' },
  { title: 'Luxury Residential Excellence', year: '2023' },
  { title: 'Commercial Anchor Recognition', year: '2022' },
  { title: 'Client Experience Citation', year: '2021' },
];

const pressFeatures = [
  { title: 'The Quiet Luxury Playbook', outlet: 'Architectural Ledger' },
  { title: 'Building for Long-Term Value', outlet: 'Civic Design Review' },
  { title: 'Land, Light, and Arrival', outlet: 'Metropolitan Homes' },
  { title: 'Premium Corridors Watchlist', outlet: 'Realty Quarterly' },
];

const investmentLens = [
  {
    title: 'Entry Timing',
    copy: 'Cycle-aware acquisition windows to reduce exposure and compress delivery risk.',
    detail: 'Tracked via corridor pricing bands and absorption rate shifts.',
  },
  {
    title: 'Yield Stability',
    copy: 'Balanced mixes of residential and commercial to stabilize cash flow.',
    detail: 'Benchmarked with pre-booking velocity and completion premiums.',
  },
  {
    title: 'Exit Readiness',
    copy: 'Clear documentation, premium detailing, and future-fit layouts.',
    detail: 'Ensures resale and leasing retain pricing power.',
  },
];

const heroSlides = [
  {
    title: 'Private residences with executive calm.',
    subtitle: 'Signature villas and boutique communities guided by disciplined planning.',
    cta: 'Explore Projects',
    to: '/projects',
    image:
      'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&q=80&w=2070',
  },
  {
    title: 'Land decisions built for longevity.',
    subtitle: 'Acquisition intelligence, phased delivery, and resilient value creation.',
    cta: 'Our Approach',
    to: '/about',
    image:
      'https://images.unsplash.com/photo-1449844908441-8829872d2607?auto=format&fit=crop&q=80&w=1600',
  },
  {
    title: 'Commercial anchors with quiet authority.',
    subtitle: 'Measured density, clear circulation, and premium material restraint.',
    cta: 'Start a Conversation',
    to: '/contact',
    image:
      'https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?auto=format&fit=crop&q=80&w=1800',
  },
];

export default function HomePage() {
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <>
      <section className="relative overflow-hidden pt-0">
        <div className="relative w-full overflow-hidden border-y border-white/10 bg-white/5 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
          <div className="relative h-[520px] md:h-[580px]">
            {heroSlides.map((slide, index) => {
              const isActive = index === activeSlide;
              return (
                <div
                  key={slide.title}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    isActive ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/35 to-transparent" />
                </div>
              );
            })}

            <div className="absolute inset-0 flex items-center">
              <div className="section-shell w-full">
                <div className="max-w-3xl px-8 md:px-12">
                  <span className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/70">
                    Executive Portfolio
                  </span>
                  <h1 className="mt-6 text-balance text-5xl leading-[0.95] text-secondary sm:text-6xl md:text-7xl">
                    {heroSlides[activeSlide].title}
                  </h1>
                  <p className="mt-6 max-w-2xl text-base leading-8 text-secondary/70 md:text-lg">
                    {heroSlides[activeSlide].subtitle}
                  </p>
                  <div className="mt-8 flex flex-wrap gap-4">
                    <Link
                      to={heroSlides[activeSlide].to}
                      className="inline-flex items-center justify-center rounded-full bg-accent px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light hover:text-primary"
                    >
                      {heroSlides[activeSlide].cta}
                    </Link>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveSlide((prev) => (prev + 1) % heroSlides.length)
                      }
                      className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:border-accent/40 hover:text-accent"
                    >
                      Next Slide
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-3">
              {heroSlides.map((_, index) => (
                <button
                  key={`slide-dot-${index}`}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => setActiveSlide(index)}
                  className={`h-2.5 w-10 rounded-full transition-all duration-300 ${
                    index === activeSlide ? 'bg-gold-light' : 'bg-white/25'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Hero />

      <section className="pb-12">
        <div className="section-shell">
          <div className="soft-panel p-8 md:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
              <div>
                <span className="editorial-kicker">Browse the studio</span>
                <h2 className="mt-7 text-balance text-4xl leading-tight sm:text-5xl">
                  Separate pages, clearer information, and a calmer route through the brand.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-secondary/65">
                  The site is now structured around the decisions people actually make first: understanding the studio, reviewing projects, and starting a conversation.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {quickLinks.map((item) => (
                  <Link
                    key={item.title}
                    to={item.to}
                    className="rounded-[1.75rem] border border-white/10 bg-white/5 p-6 shadow-[0_18px_45px_rgba(0,0,0,0.35)] transition-transform duration-300 hover:-translate-y-1"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">Page</p>
                    <h3 className="mt-4 text-3xl text-secondary">{item.title}</h3>
                    <p className="mt-3 leading-7 text-secondary/65">{item.copy}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="section-shell">
          <div className="soft-panel p-8 md:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
              <div>
                <span className="editorial-kicker">Signature approach</span>
                <h2 className="mt-7 text-balance text-4xl leading-tight sm:text-5xl">
                  Executive oversight from first land scan to final handover.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-secondary/65">
                  We run a compact decision chain across site analysis, design control, and construction delivery so the
                  finished experience feels deliberate, not improvised.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {approachPoints.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-[1.5rem] border border-white/10 bg-white/5 p-6 shadow-[0_16px_40px_rgba(0,0,0,0.32)]"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">Focus</p>
                    <h3 className="mt-4 text-2xl text-secondary">{item.title}</h3>
                    <p className="mt-3 leading-7 text-secondary/65">{item.copy}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?auto=format&fit=crop&q=80&w=1800"
                  alt="Executive residence entry sequence"
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary/70 via-primary/20 to-transparent" />
                <div className="absolute left-6 top-6 rounded-full bg-white/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/70">
                  Arrival Sequence
                </div>
              </div>
              <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80&w=1600"
                  alt="Interior material palette"
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/15 to-transparent" />
                <div className="absolute left-6 top-6 rounded-full bg-white/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/70">
                  Material Restraint
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="section-shell">
          <div className="dark-panel p-8 md:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div>
                <span className="editorial-kicker text-gold-light before:bg-gold-light">Awards</span>
                <h2 className="mt-7 text-balance text-4xl leading-tight text-secondary sm:text-5xl">
                  Recognized for restraint, delivery discipline, and enduring value.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-secondary/70">
                  Honors that reflect the studio’s focus on real-world livability, cost integrity, and longevity.
                </p>
                <div className="mt-8 overflow-hidden rounded-[1.75rem] border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=1600"
                    alt="Architectural award showcase"
                    className="h-56 w-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {awards.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-[1.5rem] border border-white/10 bg-white/6 p-6"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/80">
                      {item.year}
                    </p>
                    <h3 className="mt-3 text-2xl text-secondary">{item.title}</h3>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="section-shell">
          <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="dark-panel p-8 md:p-10">
              <span className="editorial-kicker text-gold-light before:bg-gold-light">Market focus</span>
              <h2 className="mt-6 text-balance text-4xl leading-tight text-secondary sm:text-5xl">
                Premium corridors, quiet landmarks, and disciplined entry timing.
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-8 text-secondary/70">
                We track boutique pockets and emerging luxury zones so acquisition decisions are grounded in measured
                demand, not speculative noise.
              </p>
              <div className="mt-8 overflow-hidden rounded-[1.5rem] border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1449844908441-8829872d2607?auto=format&fit=crop&q=80&w=1600"
                  alt="City corridor at dusk"
                  className="h-56 w-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>

            <div className="soft-panel p-8 md:p-10">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">Live pulse</p>
              <div className="mt-6 grid gap-4">
                {marketStats.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between rounded-[1.25rem] border border-white/10 bg-white/6 px-5 py-4"
                  >
                    <p className="text-sm uppercase tracking-[0.28em] text-secondary/60">{item.label}</p>
                    <p className="font-serif text-2xl text-secondary">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="section-shell">
          <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="soft-panel p-8 md:p-10">
              <span className="editorial-kicker">Client brief</span>
              <h2 className="mt-7 text-balance text-4xl leading-tight sm:text-5xl">
                Private, punctual, and aligned with real-life usage.
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-8 text-secondary/65">
                We work with a small roster of aligned clients each cycle, ensuring senior involvement across site visits,
                approvals, and final detailing.
              </p>
              <div className="mt-8 overflow-hidden rounded-[1.5rem] border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&q=80&w=1600"
                  alt="Private lounge interior"
                  className="h-56 w-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-[1.25rem] border border-white/10 bg-white/6 p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">Engagement</p>
                  <p className="mt-3 text-lg text-secondary">6–9 month delivery windows</p>
                </div>
                <div className="rounded-[1.25rem] border border-white/10 bg-white/6 p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">Availability</p>
                  <p className="mt-3 text-lg text-secondary">Limited annual slots</p>
                </div>
              </div>
            </div>

            <div className="dark-panel p-8 md:p-10">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/80">
                Reputation signals
              </p>
              <div className="mt-6 space-y-4">
                {pressHighlights.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-[1.5rem] border border-white/10 bg-white/5 p-5"
                  >
                    <p className="text-sm uppercase tracking-[0.28em] text-secondary/55">{item.source}</p>
                    <h3 className="mt-2 text-2xl text-secondary">{item.title}</h3>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="section-shell">
          <div className="soft-panel p-8 md:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
              <div>
                <span className="editorial-kicker">Investment lens</span>
                <h2 className="mt-7 text-balance text-4xl leading-tight sm:text-5xl">
                  Data-guided decisions for high-value acquisitions.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-secondary/65">
                  We treat every site as a capital decision, balancing entry timing, yield stability, and
                  long-term exit readiness.
                </p>
                <div className="mt-8 overflow-hidden rounded-[1.75rem] border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1600"
                    alt="Investment analysis desk"
                    className="h-56 w-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {investmentLens.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-[1.5rem] border border-white/10 bg-white/5 p-6 shadow-[0_16px_40px_rgba(0,0,0,0.3)]"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">Metric</p>
                    <h3 className="mt-3 text-2xl text-secondary">{item.title}</h3>
                    <p className="mt-3 leading-7 text-secondary/65">{item.copy}</p>
                    <p className="mt-4 text-sm text-secondary/50">{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="section-shell">
          <div className="dark-panel p-8 md:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <span className="editorial-kicker text-gold-light before:bg-gold-light">Press</span>
                <h2 className="mt-7 text-balance text-4xl leading-tight text-secondary sm:text-5xl">
                  Featured for restraint, execution, and clarity of intent.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-secondary/70">
                  Coverage highlights the studio’s precision in planning, material selection, and delivery.
                </p>
                <div className="mt-8 overflow-hidden rounded-[1.75rem] border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&q=80&w=1600"
                    alt="Press feature spread"
                    className="h-56 w-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {pressFeatures.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-[1.5rem] border border-white/10 bg-white/6 p-6"
                  >
                    <p className="text-sm uppercase tracking-[0.28em] text-secondary/55">{item.outlet}</p>
                    <h3 className="mt-2 text-2xl text-secondary">{item.title}</h3>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="section-shell">
          <div className="soft-panel p-8 md:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div>
                <span className="editorial-kicker">Client sentiment</span>
                <h2 className="mt-7 text-balance text-4xl leading-tight sm:text-5xl">
                  Trusted for discretion, clarity, and executive control.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-secondary/65">
                  We stay close to the decision-makers throughout the build, keeping scope and execution aligned to the
                  original intent.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {testimonials.map((item) => (
                  <div
                    key={item.name}
                    className="rounded-[1.5rem] border border-white/10 bg-white/5 p-6 shadow-[0_16px_40px_rgba(0,0,0,0.32)]"
                  >
                    <p className="text-sm leading-7 text-secondary/70">“{item.quote}”</p>
                    <p className="mt-5 text-sm font-semibold text-secondary">{item.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-secondary/45">{item.role}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Services />
    </>
  );
}

